import { useState, useEffect } from 'react';
import { collection, onSnapshot, addDoc, updateDoc, deleteDoc, doc, getDoc, setDoc, arrayUnion, query, where, writeBatch } from 'firebase/firestore';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import Section from './Section';
import RSVPSection from './RSVPSection';
import CreateEventModal from './CreateEventModal';
import LocationMapModal from './LocationMapModal';
import './SignupSheet.css';

export default function SignupSheet({ user, userProfile }) {
    const { partyId } = useParams();
    const navigate = useNavigate();
    const [sections, setSections] = useState([]);
    const [items, setItems] = useState([]);
    const [rsvps, setRsvps] = useState([]);
    const [isAddingSection, setIsAddingSection] = useState(false);
    const [newSectionTitle, setNewSectionTitle] = useState('');

    // Party Metadata
    const [partyData, setPartyData] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false); // Using modal for edits now
    const [showMapModal, setShowMapModal] = useState(false);
    const [isGuestView, setIsGuestView] = useState(false);

    const [showShareTooltip, setShowShareTooltip] = useState(false);
    const [userName, setUserName] = useState('');

    useEffect(() => {
        if (userProfile?.name) {
            setUserName(userProfile.name);
        }
    }, [userProfile]);

    useEffect(() => {
        if (!partyId || !user) return;

        // Real-time listener for Party Metadata
        const unsubParty = onSnapshot(doc(db, 'parties', partyId), (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                setPartyData(data);
            } else {
                setPartyData(null); // Legacy or new/url-only
            }
        });

        // Real-time listener for Sections
        const qSections = query(
            collection(db, 'sections'),
            where('partyId', '==', partyId)
        );

        const unsubscribeSections = onSnapshot(qSections, (snapshot) => {
            const sectionsData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            // Sort by createdAt
            sectionsData.sort((a, b) => {
                const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
                const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
                return dateA - dateB;
            });
            setSections(sectionsData);
        });

        // Real-time listener for Items
        const qItems = query(
            collection(db, 'items'),
            where('partyId', '==', partyId)
        );

        const unsubscribeItems = onSnapshot(qItems, (snapshot) => {
            const itemsData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            // Sort by createdAt
            itemsData.sort((a, b) => {
                const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
                const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
                return dateA - dateB;
            });
            setItems(itemsData);
        });

        // Real-time listener for RSVPs
        const qRSVPs = query(
            collection(db, 'rsvps'),
            where('partyId', '==', partyId)
        );

        const unsubscribeRSVPs = onSnapshot(qRSVPs, (snapshot) => {
            const rsvpData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setRsvps(rsvpData);
        });

        return () => {
            unsubParty();
            unsubscribeSections();
            unsubscribeItems();
            unsubscribeRSVPs();
        };
    }, [partyId]);

    // Metadata Update via Modal
    const handleUpdateEvent = async (updatedData) => {
        if (!partyId || isGuestView) return;
        const partyRef = doc(db, 'parties', partyId);

        try {
            await updateDoc(partyRef, updatedData);
            setShowEditModal(false);
        } catch (err) {
            console.error("Error updating party:", err);
            alert("Failed to update party");
        }
    };

    // Helper to ensure participant
    const ensureParticipant = async () => {
        if (!user || !partyData || isGuestView) return;
        if (!partyData.participants || !partyData.participants.includes(user.uid)) {
            await updateDoc(doc(db, 'parties', partyId), {
                participants: arrayUnion(user.uid)
            });
        }
    };

    const handleAddSection = async (e) => {
        e.preventDefault();
        if (isGuestView) return;
        if (newSectionTitle.trim()) {
            await addDoc(collection(db, 'sections'), {
                title: newSectionTitle,
                partyId,
                createdAt: new Date()
            });
            setNewSectionTitle('');
            setIsAddingSection(false);
        }
    };

    const handleAddItem = async (sectionId, itemName) => {
        if (isGuestView) return;
        ensureParticipant(); // Add to participants list

        let assignee = '';
        let assigneeUid = null;

        // Potluck Mode: Guests automatically claim the items they create
        // Host creates unassigned items
        const isHost = user?.uid === partyData?.creatorId;
        if ((partyData?.mode === 'POTLUCK' || !partyData?.mode) && !isHost) {
            assignee = userName || 'Anonymous';
            assigneeUid = user?.uid;
        }

        await addDoc(collection(db, 'items'), {
            sectionId,
            name: itemName,
            assignee: assignee,
            assigneeUid: assigneeUid,
            partyId,
            creatorId: user?.uid || 'unknown',
            createdAt: new Date()
        });
    };

    const handleUpdateItem = async (sectionId, itemId, updates) => {
        if (isGuestView) return;
        const itemRef = doc(db, 'items', itemId);
        await updateDoc(itemRef, updates);
    };

    const handleDeleteItem = async (sectionId, itemId) => {
        if (isGuestView) return;
        await deleteDoc(doc(db, 'items', itemId));
    };

    const handleUpdateSection = async (sectionId, newTitle) => {
        if (isGuestView) return;
        const sectionRef = doc(db, 'sections', sectionId);
        await updateDoc(sectionRef, { title: newTitle });
    };

    const handleDeleteSection = async (sectionId) => {
        if (isGuestView) return;
        console.log("Attempting to delete section:", sectionId);
        try {
            const batch = writeBatch(db);

            // Delete items in this section
            const sectionItems = items.filter(item => item.sectionId === sectionId);
            sectionItems.forEach(item => {
                batch.delete(doc(db, 'items', item.id));
            });

            // Delete section
            batch.delete(doc(db, 'sections', sectionId));

            await batch.commit();
        } catch (err) {
            console.error("Error deleting section:", err);
            alert("Failed to delete section: " + err.message);
        }
    };

    const handleShare = () => {
        const url = window.location.href;
        navigator.clipboard.writeText(url).then(() => {
            setShowShareTooltip(true);
            setTimeout(() => setShowShareTooltip(false), 2000);
        });
    };

    const handleDeleteParty = async () => {
        if (isGuestView) return;
        try {
            const batch = writeBatch(db);

            // Delete items
            items.forEach(item => {
                batch.delete(doc(db, 'items', item.id));
            });

            // Delete sections
            sections.forEach(section => {
                batch.delete(doc(db, 'sections', section.id));
            });

            // Delete RSVPs
            rsvps.forEach(rsvp => {
                batch.delete(doc(db, 'rsvps', rsvp.id));
            });

            // Delete party doc
            batch.delete(doc(db, 'parties', partyId));

            await batch.commit();
            navigate('/', { replace: true });
        } catch (err) {
            console.error("Error deleting party:", err);
            alert("Failed to delete party: " + err.message);
        }
    };

    const formatDateDisplay = (dateString, timeString) => {
        if (!dateString) return null;
        // Parse manually to avoid UTC conversion issues
        const [y, m, d] = dateString.split('-').map(Number);
        // Create local date (month is 0-indexed)
        const date = new Date(y, m - 1, d);
        const formattedDate = date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });

        if (timeString) {
            const [hours, minutes] = timeString.split(':');
            const timeDate = new Date();
            timeDate.setHours(hours);
            timeDate.setMinutes(minutes);
            const timeStr = timeDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
            return `${formattedDate} • 🕒 ${timeStr}`;
        }

        return formattedDate;
    };

    // Merge items into sections for rendering
    const sectionsWithItems = sections.map(section => ({
        ...section,
        items: items.filter(item => item.sectionId === section.id)
    }));

    return (
        <div className="signup-sheet">

            <header className="event-header-card">
                <div className="header-top-row">
                    {/* Primary Actions (Top Right) */}
                    <div className="header-actions">
                        {partyData?.creatorId === user?.uid && !isGuestView && (
                            <button
                                onClick={() => setShowEditModal(true)}
                                className="icon-action-btn"
                                title="Edit Party"
                            >
                                ✏️
                            </button>
                        )}
                        {partyData?.creatorId === user?.uid && (
                            <button
                                onClick={() => setIsGuestView(!isGuestView)}
                                className="icon-action-btn"
                                title={isGuestView ? 'Switch to Host View' : 'Switch to Guest View'}
                            >
                                {isGuestView ? '👑' : '👁️'}
                            </button>
                        )}
                    </div>
                </div>

                <div className="title-section">
                    <div className="event-emoji">{partyData?.emoji || "🎉"}</div>
                    <h1 className="event-title">{partyData?.name || "Revlry Party"}</h1>
                </div>

                <div className="details-row">
                    {partyData?.date && (
                        <div className="detail-item">
                            📅 {formatDateDisplay(partyData.date, partyData.time)}
                        </div>
                    )}
                    <div className="detail-item">
                        👤 Hosted by {partyData?.creatorName || "Unknown"}
                    </div>
                </div>

                {partyData?.description && (
                    <div className="description-section">
                        <p>{partyData.description}</p>
                    </div>
                )}

                {partyData?.location && (
                    <div className="location-section">
                        <p className="location-address">📍 {partyData.location.address}</p>

                        <div className="map-buttons-row">
                            <button className="map-pill-btn view-map-btn" onClick={() => setShowMapModal(true)}>
                                <span>🗺️ View Map</span>
                            </button>
                            <button
                                className="map-pill-btn"
                                onClick={() => window.open(`http://maps.apple.com/?q=${encodeURIComponent(partyData.location.address)}`, '_blank')}
                            >
                                <svg className="brand-icon" viewBox="0 0 384 512" fill="currentColor" height="1em" width="1em">
                                    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 52.3-11.4 69.5-34.3z" />
                                </svg>
                                <span>Apple Maps</span>
                            </button>
                            <button
                                className="map-pill-btn"
                                onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(partyData.location.address)}`, '_blank')}
                            >
                                <svg className="brand-icon" viewBox="0 0 24 24" fill="currentColor" height="1em" width="1em">
                                    <path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .533 5.333.533 12S5.867 24 12.48 24c3.44 0 6.013-1.133 8.053-3.24 2.107-2.107 2.773-5.2 2.773-7.747 0-.76-.067-1.48-.187-2.093h-10.64z" />
                                </svg>
                                <span>Google Maps</span>
                            </button>
                        </div>
                    </div>
                )}

                <div className="share-section">
                    <button onClick={handleShare} className="map-pill-btn share-invite-btn">
                        🔗 Copy Invite Link
                        {showShareTooltip && <span className="tooltip tooltip-top">Copied!</span>}
                    </button>
                </div>
            </header>

            <RSVPSection
                partyId={partyId}
                user={user}
                userProfile={userProfile}
                isHost={user?.uid === partyData?.creatorId && !isGuestView}
                guestLimit={partyData?.guestLimit}
                isGuestView={isGuestView}
            />

            {rsvps.length > 0 && (
                <div className="guest-list-summary">
                    <h3>Who's coming</h3>
                    <div className="guest-pills">
                        {rsvps.filter(r => r.status === 'Yes').map(r => (
                            <span key={r.id} className="guest-pill yes">
                                {r.userId === partyData?.creatorId && "👑 "}{r.name}{r.plusGuests > 0 ? ` +${r.plusGuests}` : ''}
                            </span>
                        ))}
                        {rsvps.filter(r => r.status === 'Maybe').map(r => (
                            <span key={r.id} className="guest-pill maybe">
                                {r.userId === partyData?.creatorId && "👑 "}{r.name}{r.plusGuests > 0 ? ` +${r.plusGuests}` : ''} (maybe)
                            </span>
                        ))}
                        {rsvps.filter(r => r.status === 'No').map(r => (
                            <span key={r.id} className="guest-pill no">
                                {r.name} (can't make it)
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {partyData?.mode !== 'RSVP' && (
                <div className="sections-container">
                    {sectionsWithItems.map(section => (
                        <Section
                            key={section.id}
                            section={section}
                            onAddItem={(name) => handleAddItem(section.id, name)}
                            onUpdateItem={(itemId, updates) => handleUpdateItem(section.id, itemId, updates)}
                            onDeleteItem={(itemId) => handleDeleteItem(section.id, itemId)}
                            onUpdateSection={(newTitle) => handleUpdateSection(section.id, newTitle)}
                            onDeleteSection={() => handleDeleteSection(section.id)}
                            isHost={user?.uid === partyData?.creatorId && !isGuestView}
                            user={user}
                            userProfile={userProfile}
                            mode={partyData?.mode || 'POTLUCK'}
                            participants={rsvps}
                        />
                    ))}
                </div>
            )}

            {/* Add Section Button: Hidden in RSVP mode. Host only in SIGNUP mode. Everyone in POTLUCK mode. */}
            {partyData?.mode !== 'RSVP' && (
                (partyData?.mode !== 'SIGNUP' || (user?.uid === partyData?.creatorId && !isGuestView)) && (
                    <div className="add-section-container" style={{ display: 'flex', justifyContent: 'center' }}>
                        {isAddingSection ? (
                            <form onSubmit={handleAddSection} className="add-section-form">
                                <input
                                    type="text"
                                    placeholder="New Section Name (e.g. Desserts)"
                                    value={newSectionTitle}
                                    onChange={(e) => setNewSectionTitle(e.target.value)}
                                    autoFocus
                                    className="new-section-input"
                                />
                                <div className="form-actions">
                                    <button type="submit" className="confirm-btn">Create Section</button>
                                    <button
                                        type="button"
                                        className="cancel-btn"
                                        onClick={() => setIsAddingSection(false)}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        ) : (
                            <button className="add-section-btn" onClick={() => setIsAddingSection(true)}>
                                + Add New Section
                            </button>
                        )}
                    </div>
                )
            )}

            <CreateEventModal
                isOpen={showEditModal}
                onClose={() => setShowEditModal(false)}
                isEditMode={true}
                initialData={partyData}
                onUpdate={handleUpdateEvent}
                onDelete={handleDeleteParty}
            />

            <LocationMapModal
                isOpen={showMapModal}
                onClose={() => setShowMapModal(false)}
                address={partyData?.location?.address}
                apiKey="AIzaSyByMSIR9nzJqeVwch2lb1fnZ8B-iQs6ZTQ"
            />
        </div>
    );
}
